"use client";

import { Button } from "@/components/ui/button";
import { MessageActionHandlers } from "./hooks/useMessageActionHandlers";
import {
  LucideIcon,
  Reply,
  Forward,
  Trash2,
  Archive,
  MailOpen,
  Mail,
  CheckSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface ActionButtonConfig {
  action: keyof MessageActionHandlers;
  icon: LucideIcon;
  label?: string;
  title?: string;
  variant?: "ghost" | "outline" | "default" | "destructive" | "secondary";
  className?: string;
  showLabel?: boolean;
}

interface ActionButtonProps {
  config: ActionButtonConfig;
  handlers: MessageActionHandlers;
  className?: string;
  size?: "sm" | "default" | "lg";
}

export function ActionButton({ config, handlers, className, size = "sm" }: ActionButtonProps) {
  const {
    action,
    icon: Icon,
    label,
    title,
    variant = "ghost",
    className: configClassName,
    showLabel = false,
  } = config;

  // Get the handler function
  const handler = handlers[action] as () => void;

  if (!handler) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`ActionButton: Handler for action "${action}" not found`);
    }
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handler}
      title={title || label}
      disabled={handlers.isActionLoading}
      className={cn(configClassName, className)}
    >
      <Icon className={cn("h-4 w-4", showLabel && "mr-2")} />
      {showLabel && label}
    </Button>
  );
}

// Pre-configured action buttons for consistency
export const ACTION_CONFIGS: Record<string, ActionButtonConfig> = {
  reply: {
    action: "handleReply",
    icon: Reply,
    label: "Reply",
    title: "Reply to this message",
    variant: "outline",
  },
  forward: {
    action: "handleForward",
    icon: Forward,
    label: "Forward",
    title: "Forward this message",
    variant: "outline",
  },
  createTask: {
    action: "handleCreateTask",
    icon: CheckSquare,
    label: "Create Task",
    title: "Create a task from this message",
    variant: "outline",
  },
  delete: {
    action: "handleDelete",
    icon: Trash2,
    label: "Delete",
    title: "Delete this message",
    variant: "ghost",
    className: "text-destructive hover:text-destructive",
  },
  archive: {
    action: "handleArchive",
    icon: Archive,
    label: "Archive",
    title: "Archive this message",
    variant: "ghost",
  },
  markRead: {
    action: "handleMarkRead",
    icon: MailOpen,
    label: "Mark as Read",
    title: "Mark as read",
    variant: "ghost",
  },
  markUnread: {
    action: "handleMarkUnread",
    icon: Mail,
    label: "Mark as Unread",
    title: "Mark as unread",
    variant: "ghost",
  },
};
